import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePublicLanguage, usePublicTr } from '../contexts/PublicLanguageContext';
import { formatMoney, monthlyEquivalentMoney, usePricing, yearlySavingPercent } from '../hooks/usePricing';
import CheckoutModal from './CheckoutModal';
import './Pricing.css';

const Pricing: React.FC = () => {
  const tr = usePublicTr();
  const { language } = usePublicLanguage();
  const navigate = useNavigate();
  // Prices are banded by the visitor's country, so they come from the server
  // rather than the page. Until the request lands this shows band A.
  const { pricing } = usePricing();
  // Written with its own currency: a Mexican visitor is quoted $109 MXN and
  // a Costa Rican ₡2.900, not a dollar figure they have to convert.
  const monthly = formatMoney(pricing.monthly, pricing.currency, language);
  const yearly = formatMoney(pricing.yearly, pricing.currency, language);
  const perMonth = monthlyEquivalentMoney(pricing, language);
  const free = formatMoney(0, pricing.currency, language);
  const saving = yearlySavingPercent(pricing);
  const [checkoutPlan, setCheckoutPlan] = useState<'monthly' | 'yearly' | null>(null);
  const [subscribed, setSubscribed] = useState(false);
  const isAuthenticated = !!localStorage.getItem('token');

  // Closing the checkout after a successful subscription returns the user to
  // the app (they arrived here from the public-styled pricing page). Closing
  // without subscribing just dismisses the modal and stays on pricing.
  const closeCheckout = () => {
    setCheckoutPlan(null);
    if (subscribed) {
      setSubscribed(false);
      navigate('/');
    }
  };

  // Paid plans need an account first; the checkout itself runs in-page.
  const startCheckout = (plan: 'monthly' | 'yearly') => {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }
    setCheckoutPlan(plan);
  };

  return (
    <div className="pricing-container">
      {/* Header Section */}
      <section className="pricing-header">
        <div className="pricing-header-content">
          <div className="pricing-logo-placeholder">
            <div className="logo-box">
              <img src="/logo192.png" alt="Yarnboard" className="brand-logo-img" />
            </div>
          </div>
          <h1>{tr('Pricing', 'Precios', 'Preços')}</h1>
          <p className="pricing-tagline">
            {tr('Choose the perfect plan for your investigation', 'Elige el plan perfecto para tu investigación', 'Escolha o plano perfeito para a sua investigação')}
          </p>
        </div>
      </section>

      {/* Pricing Cards Section */}
      <section className="pricing-cards-section">
        <div className="pricing-content">
          <div className="pricing-grid">
            {/* Free Plan */}
            <div className="pricing-card">
              <div className="plan-header">
                <h3>{tr('Free', 'Gratis', 'Grátis')}</h3>
                <div className="price-group">
                  <div className="price">
                    <span className="amount">{free}</span>
                    <span className="period">/{tr('forever', 'siempre', 'sempre')}</span>
                  </div>
                </div>
              </div>

              <div className="plan-features">
                <h4>{tr('To Get Started:', 'Para Empezar:', 'Para Começar:')}</h4>
                <ul>
                  <li>✓ {tr('Up to 50 notes', 'Hasta 50 notas', 'Até 50 notas')}</li>
                  <li>✓ {tr('Up to 5 documents', 'Hasta 5 documentos', 'Até 5 documentos')}</li>
                  <li>✓ {tr('Up to 2 storyboards', 'Hasta 2 guiones gráficos', 'Até 2 storyboards')}</li>
                  <li>✓ {tr('1GB storage', 'Almacenamiento de 1GB', '1GB de armazenamento')}</li>
                  <li>✓ {tr('Search', 'Búsqueda', 'Busca')}</li>
                  <li>✓ {tr('15 versions of every document', '15 versiones de cada documento', '15 versões de cada documento')}</li>
                  <li>✓ {tr('Manual tagging', 'Etiquetado manual', 'Etiquetamento manual')}</li>
                  <li>✗ {tr('No image OCR', 'Sin OCR en imágenes', 'Sem OCR em imagens')}</li>
                  <li>✗ {tr('No AI tagging', 'Sin etiquetado IA', 'Sem etiquetamento por IA')}</li>
                  <li>✗ {tr('No Discovery graph', 'Sin gráfico de descubrimiento', 'Sem grafo do Descobrir')}</li>
                  <li>✗ {tr('No document import', 'Sin importar documentos', 'Sem importação de documentos')}</li>
                  <li>✗ {tr('No sharing with collaborators', 'Sin compartir con colaboradores', 'Sem compartilhar com colaboradores')}</li>
                </ul>
              </div>

              <Link to={isAuthenticated ? '/' : '/register'} className="plan-button">
                {isAuthenticated
                  ? (tr('Your current plan', 'Tu plan actual', 'Seu plano atual'))
                  : (tr('Start free', 'Comenzar gratis', 'Começar grátis'))}
              </Link>
            </div>

            {/* Monthly Plan */}
            <div className="pricing-card">
              <div className="plan-header">
                <h3>{tr('Monthly', 'Mensual', 'Mensal')}</h3>
                <div className="price-group">
                  <div className="price">
                    <span className="amount">{monthly}</span>
                    <span className="period">/{tr('mo', 'mes', 'mês')}</span>
                  </div>
                </div>
              </div>

              <div className="plan-features">
                <h4>{tr('Includes:', 'Incluye:', 'Inclui:')}</h4>
                <ul>
                  <li>✓ {tr('Unlimited notes', 'Notas ilimitadas', 'Notas ilimitadas')}</li>
                  <li>✓ {tr('Unlimited documents', 'Documentos ilimitados', 'Documentos ilimitados')}</li>
                  <li>✓ {tr('Unlimited storyboards', 'Guiones gráficos ilimitados', 'Storyboards ilimitados')}</li>
                  <li>✓ {tr('15GB storage', 'Almacenamiento de 15GB', '15GB de armazenamento')}</li>
                  <li>✓ {tr('Image OCR', 'OCR en imágenes', 'OCR em imagens')}</li>
                  <li>✓ {tr('AI-powered tagging', 'Etiquetado con IA', 'Etiquetamento com IA')}</li>
                  <li>✓ {tr('Discovery graph', 'Gráfico de descubrimiento', 'Grafo do Descobrir')}</li>
                  <li>✓ {tr('Google Docs & file import', 'Importar Google Docs y archivos', 'Importar Google Docs e arquivos')}</li>
                  <li>✓ {tr('Share storyboards, documents and notes with collaborators', 'Comparte guiones gráficos, documentos y notas con colaboradores', 'Compartilhe storyboards, documentos e notas com colaboradores')}</li>
                  <li>✓ {tr('Full-text search', 'Búsqueda de texto completo', 'Busca em texto completo')}</li>
                  <li>✓ {tr('Screenplay writing', 'Escritura de guiones', 'Escrita de roteiros')}</li>
                  <li>✓ {tr('15 versions of every document', '15 versiones de cada documento', '15 versões de cada documento')}</li>
                </ul>
              </div>

              <button className="plan-button" onClick={() => startCheckout('monthly')}>
                {tr('Start 30-day free trial', 'Iniciar prueba de 30 días', 'Iniciar teste grátis de 30 dias')}
              </button>
              <p className="plan-trial-note">
                {tr(`Free for 30 days, then $${monthly}/mo`, `Gratis por 30 días, luego $${monthly}/mes`, `Grátis por 30 dias, depois US$ ${monthly}/mês`)}
              </p>
            </div>

            {/* Yearly Plan */}
            <div className="pricing-card featured">
              <div className="best-value-badge">
                {tr('BEST VALUE!', '¡MEJOR VALOR!', 'MELHOR CUSTO-BENEFÍCIO!')}
              </div>
              <div className="plan-header">
                <h3>{tr('Yearly', 'Anual', 'Anual')}</h3>
                <div className="savings">
                  {tr(`Save ${saving}%`, `Ahorra ${saving}%`, `Economize ${saving}%`)}
                </div>
                <div className="price-group">
                  <div className="price">
                    <span className="amount">{yearly}</span>
                    <span className="period">/{tr('yr', 'año', 'ano')}</span>
                  </div>
                </div>
                <p className="price-breakdown">
                  {tr(`Just ${perMonth}/mo`, `Solo ${perMonth}/mes`, `Só ${perMonth}/mês`)}
                </p>
              </div>

              <div className="plan-features">
                <h4>{tr('Everything, at a discount:', 'Todo, con descuento:', 'Tudo, com desconto:')}</h4>
                <ul>
                  <li>✓ {tr('Unlimited notes', 'Notas ilimitadas', 'Notas ilimitadas')}</li>
                  <li>✓ {tr('Unlimited documents', 'Documentos ilimitados', 'Documentos ilimitados')}</li>
                  <li>✓ {tr('Unlimited storyboards', 'Guiones gráficos ilimitados', 'Storyboards ilimitados')}</li>
                  <li>✓ {tr('15GB storage', 'Almacenamiento de 15GB', '15GB de armazenamento')}</li>
                  <li>✓ {tr('Image OCR', 'OCR en imágenes', 'OCR em imagens')}</li>
                  <li>✓ {tr('AI-powered tagging', 'Etiquetado con IA', 'Etiquetamento com IA')}</li>
                  <li>✓ {tr('Discovery graph', 'Gráfico de descubrimiento', 'Grafo do Descobrir')}</li>
                  <li>✓ {tr('Google Docs & file import', 'Importar Google Docs y archivos', 'Importar Google Docs e arquivos')}</li>
                  <li>✓ {tr('Share storyboards, documents and notes with collaborators', 'Comparte guiones gráficos, documentos y notas con colaboradores', 'Compartilhe storyboards, documentos e notas com colaboradores')}</li>
                  <li>✓ {tr('Full-text search', 'Búsqueda de texto completo', 'Busca em texto completo')}</li>
                  <li>✓ {tr('Screenplay writing', 'Escritura de guiones', 'Escrita de roteiros')}</li>
                  <li>✓ {tr('15 versions of every document', '15 versiones de cada documento', '15 versões de cada documento')}</li>
                </ul>
              </div>

              <button className="plan-button featured" onClick={() => startCheckout('yearly')}>
                {tr('Start 30-day free trial', 'Iniciar prueba de 30 días', 'Iniciar teste grátis de 30 dias')}
              </button>
              <p className="plan-trial-note">
                {tr(`Free for 30 days, then $${yearly}/yr`, `Gratis por 30 días, luego $${yearly}/año`, `Grátis por 30 dias, depois US$ ${yearly}/ano`)}
              </p>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="pricing-faq">
            <h2>{tr('Frequently Asked Questions', 'Preguntas Frecuentes', 'Perguntas Frequentes')}</h2>
            
            <div className="faq-item">
              <h4>{tr('Can I switch plans?', '¿Puedo cambiar de plan?', 'Posso trocar de plano?')}</h4>
              <p>
                {tr('Yes, you can switch between monthly and yearly at any time. Changes apply at your next billing cycle.', 'Sí, puedes cambiar entre mensual y anual en cualquier momento. Los cambios se aplican en tu próximo ciclo de facturación.', 'Pode, sim. Dá para alternar entre mensal e anual quando quiser. As mudanças valem a partir do próximo ciclo de cobrança.')}
              </p>
            </div>

            <div className="faq-item">
              <h4>{tr('What payment methods do you accept?', '¿Qué métodos de pago aceptan?', 'Quais formas de pagamento vocês aceitam?')}</h4>
              <p>
                {tr('We accept major credit/debit cards (Visa, Mastercard, Amex) and Costa Rican local payment processors.', 'Aceptamos tarjetas de crédito/débito principales (Visa, Mastercard, Amex) y procesadores de pago locales de Costa Rica.', 'Aceitamos os principais cartões de crédito e débito (Visa, Mastercard, Amex) e processadores de pagamento locais da Costa Rica.')}
              </p>
            </div>

            <div className="faq-item">
              <h4>{tr('Is there a free trial?', '¿Hay una prueba gratuita?', 'Existe teste grátis?')}</h4>
              <p>
                {tr('Yes. Every subscription starts with 30 days free. You aren\'t charged until the trial ends, and you can cancel anytime before then at no cost.', 'Sí. Cada suscripción comienza con 30 días gratis. No se te cobra hasta que termina la prueba, y puedes cancelar en cualquier momento antes de eso sin costo.', 'Existe. Toda assinatura começa com 30 dias grátis. Você não é cobrado até o teste acabar, e pode cancelar a qualquer momento antes disso sem custo.')}
              </p>
            </div>

            <div className="faq-item">
              <h4>{tr('Can I cancel anytime?', '¿Puedo cancelar en cualquier momento?', 'Posso cancelar quando quiser?')}</h4>
              <p>
                {tr('Absolutely. No long-term contracts. Cancel anytime from your account settings.', 'Absolutamente. No hay contratos a largo plazo. Cancela en cualquier momento desde la configuración de tu cuenta.', 'Com certeza. Sem contratos de longo prazo. Cancele quando quiser nas configurações da sua conta.')}
              </p>
            </div>
          </div>

          {/* CTA Section */}
          <div className="pricing-cta">
            <h2>{tr('Ready to Get Started?', '¿Listo para Comenzar?', 'Pronto para Começar?')}</h2>
            <p>
              {tr('Start free today, or try any paid plan free for 30 days.', 'Comienza gratis hoy, o prueba cualquier plan de pago gratis por 30 días.', 'Comece grátis hoje, ou teste qualquer plano pago de graça por 30 dias.')}
            </p>
            <Link to={isAuthenticated ? '/' : '/register'} className="cta-button primary large">
              {tr('Get Started Now', 'Comenzar Ahora', 'Comece Agora')}
            </Link>
          </div>
        </div>
      </section>

      {checkoutPlan && (
        <CheckoutModal
          plan={checkoutPlan}
          onClose={closeCheckout}
          onSuccess={() => setSubscribed(true)}
        />
      )}
    </div>
  );
};

export default Pricing;
